import { useCallback, useSyncExternalStore } from 'react'
import { isTerminalThemeEnabled } from './terminalTheme'

/**
 * The signed-in app's light/dark choice.
 *
 * 'dark' is today's terminal look and the default; 'light' is the light
 * palette (theme/light-theme.css); 'auto' follows the device's
 * prefers-color-scheme and switches live when the device does. There is no
 * time-of-day or location logic: if a device switches at sunset, Auto does too.
 *
 * Only Layout applies the result to <html>. Public pages (PublicShell) and the
 * sign-in pages ignore it, so a shared link or the login screen looks the same
 * to everyone whatever this is set to.
 *
 * Built with REACT_APP_TERMINAL_THEME=off there is no dark theme to choose:
 * the resolved theme is always light and the control is hidden.
 *
 * The choice lives in a small module store rather than a context, so Layout,
 * the Dashboard and the sidebar control stay in step without a provider.
 */
export type ThemePreference = 'dark' | 'light' | 'auto'
export type ResolvedTheme = 'dark' | 'light'

export const THEME_PREFERENCES: readonly ThemePreference[] = ['dark', 'light', 'auto']
export const THEME_STORAGE_KEY = 'fc-theme'
export const DEFAULT_THEME_PREFERENCE: ThemePreference = 'dark'
const DEVICE_DARK_QUERY = '(prefers-color-scheme: dark)'

const isPreference = (value: unknown): value is ThemePreference =>
  typeof value === 'string' && (THEME_PREFERENCES as readonly string[]).includes(value)

/** The stored choice; anything unreadable or unknown is dark. */
export function readStoredPreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY)
    return isPreference(stored) ? stored : DEFAULT_THEME_PREFERENCE
  } catch {
    return DEFAULT_THEME_PREFERENCE
  }
}

// null until first read, so a fresh page load (or a test reset) reads storage.
let preference: ThemePreference | null = null
const listeners = new Set<() => void>()

const currentPreference = (): ThemePreference => {
  if (preference === null) preference = readStoredPreference()
  return preference
}

const notify = () => listeners.forEach((listener) => listener())

/** Choose a theme. Kept in memory for the session even if storage is blocked. */
export function setThemePreference(next: ThemePreference): void {
  if (!isPreference(next)) return
  preference = next
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, next)
  } catch {
    // Private mode / blocked storage: the choice lasts until reload.
  }
  notify()
}

const deviceQuery = (): MediaQueryList | null => {
  try {
    return typeof window.matchMedia === 'function' ? window.matchMedia(DEVICE_DARK_QUERY) : null
  } catch {
    return null
  }
}

// Without matchMedia there is no device signal, so Auto keeps the default.
const deviceIsDark = (): boolean => deviceQuery()?.matches ?? true

/**
 * One device listener while anything is subscribed; removed with the last
 * subscriber. Preference changes go through `listeners` directly.
 */
let mediaCleanup: (() => void) | null = null

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  if (listeners.size === 1) {
    const query = deviceQuery()
    if (query) {
      const onChange = () => notify()
      if (typeof query.addEventListener === 'function') {
        query.addEventListener('change', onChange)
        mediaCleanup = () => query.removeEventListener('change', onChange)
      } else if (typeof query.addListener === 'function') {
        // Safari < 14.
        query.addListener(onChange)
        mediaCleanup = () => query.removeListener(onChange)
      }
    }
  }
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0 && mediaCleanup) {
      mediaCleanup()
      mediaCleanup = null
    }
  }
}

export const resolveTheme = (choice: ThemePreference, deviceDark: boolean): ResolvedTheme =>
  choice === 'auto' ? (deviceDark ? 'dark' : 'light') : choice

export interface ThemePreferenceState {
  /** What the user picked. */
  preference: ThemePreference
  /** What is shown: Auto resolved against the device. Always light when the dark theme is built out. */
  resolved: ResolvedTheme
  setPreference: (next: ThemePreference) => void
  /** False when built with REACT_APP_TERMINAL_THEME=off: there is nothing to choose. */
  canChoose: boolean
}

export function useThemePreference(): ThemePreferenceState {
  const choice = useSyncExternalStore(subscribe, currentPreference, () => DEFAULT_THEME_PREFERENCE)
  const deviceDark = useSyncExternalStore(subscribe, deviceIsDark, () => true)
  const setPreference = useCallback((next: ThemePreference) => setThemePreference(next), [])
  const canChoose = isTerminalThemeEnabled()
  return {
    preference: choice,
    resolved: canChoose ? resolveTheme(choice, deviceDark) : 'light',
    setPreference,
    canChoose,
  }
}

/** Tests only: forget the in-memory choice so the next read comes from storage. */
export function resetThemePreferenceForTests(): void {
  preference = null
}
